import classNames from 'classnames';
import { inflect } from 'inflection';
import * as _ from 'lodash-es';
import React, { useCallback, useMemo, useReducer, useState } from 'react';
import type { GroupBase, InputActionMeta, InputProps } from 'react-select';
import { components as selectComponents } from 'react-select';
import Select from '../../components/Select';
import type { SelectProps, ValueType } from '../../components/Select';
import { PostType } from '../../graphql/generated/graphql';
import { useAllGroupMap } from '../../groups/useAllGroups';
import { useAllSlimPostMap } from '../../post/useAllSlimPosts';
import { useAllTopicMap } from '../../topic/useAllTopics';
import { useAllUsersMap } from '../../user/useAllUsers';
import * as styles from './EntitySelect.module.scss';
import EntitySelectCategoryHeading from './EntitySelectCategoryHeading';
import EntitySelectCategoryOption from './EntitySelectCategoryOption';
import EntitySelectMenuList from './EntitySelectMenuList';
import EntitySelectMultiValue from './EntitySelectMultiValue';
import EntitySelectNoOptionsMessage from './EntitySelectNoOptionsMessage';
import type { NewSelectOptionProps } from './NewSelectOption';
import type {
  BaseSearchConfig,
  LocalEntitySearchType,
} from './localEntitySearch';
import {
  groupToEntity,
  postToEntity,
  topicToEntity,
  userToEntity,
} from './localEntitySearch';
import type {
  FullPostData,
  UseEntitySearchConfig,
  UseEntitySearchGroupResult,
  UseEntitySearchPostResult,
  UseEntitySearchTopicResult,
  UseEntitySearchUserResult,
} from './useEntitySearch';
import useEntitySearch from './useEntitySearch';

const NonHiddenInput = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: InputProps<Option, IsMulti, Group>,
) => <selectComponents.Input {...props} isHidden={false} />;

const ENTITY_SEARCH_OPTIONS = { limitPerType: 100 };

export type EntitySelectConfig = {
  [K in keyof Omit<
    UseEntitySearchConfig,
    'order'
  >]: UseEntitySearchConfig[K] & {
    label?: string;
    preferredMaxCount?: number;
    showIfNoOthersMatch?: boolean;
  };
} & {
  template?: Pick<BaseSearchConfig<FullPostData>, 'filter'> & {
    label?: string;
    preferredMaxCount?: number;
    showIfNoOthersMatch?: boolean;
  };
  new?: {
    preferredMaxCount?: number;
    showIfNoOthersMatch?: boolean;
    getGroup?: (input: string) => {
      label: string;
      options: EntitySelectNewOptionValues[];
    } | null;
    label?: string;
  };
} & {
  order?: (LocalEntitySearchType | 'template' | 'new')[];
};

type EntitySearchGroupResult = UseEntitySearchGroupResult;
type EntitySearchPostResult = UseEntitySearchPostResult;
type EntitySearchTopicResult = UseEntitySearchTopicResult;
type EntitySearchUserResult = UseEntitySearchUserResult;

interface EntitySearchNewResult extends NewSelectOptionProps {
  id: string;
  type: 'new';
}
type BaseEntitySearchResult =
  | EntitySearchGroupResult
  | EntitySearchPostResult
  | EntitySearchTopicResult
  | EntitySearchUserResult
  | EntitySearchNewResult;

type EntitySelectCategory = BaseEntitySearchResult['type'] | 'template';

export type EntitySelectNewOptionValues = NewSelectOptionProps & {
  value: string;
  onSelect: () => void;
};

export type EntitySelectCategorizedSearchResult =
  | (Exclude<BaseEntitySearchResult, EntitySearchNewResult> & {
      onSelect?: never;
      isValid?: never;
    })
  | (EntitySearchNewResult & EntitySelectNewOptionValues);

type BaseValue = Pick<EntitySelectCategorizedSearchResult, 'id' | 'type'>;

export interface EntityCategoryOption
  extends GroupBase<EntitySelectCategorizedSearchResult> {
  label: string;
  options: EntitySelectCategorizedSearchResult[];
  newOption?: EntitySelectCategorizedSearchResult;
  type: EntitySelectCategory;
  sliced: boolean;
}

// Remove fields that are unnecessary to render <PostBreadcrumbs/>
// to improve performance and suppress warnings from <Select/> because
// topic.ancestors contains circular refs which are not supported by
// react-fast-compare used by <Select/>.
const pruneTopicFields = _.memoize((topic) => ({
  ...topic,
  ancestors: topic.ancestors.map((ancestor: { id: string; name: string }) =>
    _.pick(ancestor, ['id', 'name']),
  ),
}));

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export function balanceCategories<O>({
  maxOptionsToShow,
  categoryOptions,
  config,
}: {
  maxOptionsToShow: number;
  categoryOptions: { type: EntitySelectCategory; options: O[] }[];
  config: EntitySelectConfig;
}) {
  const order = config.order ?? categoryOptions.map((o) => o.type);

  // Initial setup based on the preferredMaxCount or average options count.
  // After this step, there might be remaining positions to fill,
  // due to the lack of options in some categories.
  const { sum, ...balanceSetup } = order.reduce<
    {
      [key in (typeof order)[number]]?: {
        availableOptionsCount: number;
        count: number;
      };
    } & { sum: number }
  >(
    (acc, key, index) => {
      const remainingOptions = maxOptionsToShow - acc.sum;
      const averageOptionsCount = Math.ceil(
        remainingOptions / (categoryOptions.length - index),
      );

      const availableOptionsCount =
        categoryOptions.find((o) => o.type === key)?.options.length ?? 0;
      const maxCount = config[key]?.preferredMaxCount ?? averageOptionsCount;
      const count = config[key]?.showIfNoOthersMatch
        ? 0
        : Math.min(availableOptionsCount, maxCount, remainingOptions);

      return {
        ...acc,
        [key]: { availableOptionsCount, count },
        sum: acc.sum + count,
      };
    },
    { sum: 0 },
  );

  const remaining = maxOptionsToShow - sum;

  // Equally distribute remaining positions to categories,
  // or show categories that have showIfNoOthersMatch if no other items
  _.range(remaining).forEach(() => {
    const min = _.minBy(order, (key) => {
      const categorySetup = balanceSetup[key];
      if (
        categorySetup &&
        !config[key]?.showIfNoOthersMatch &&
        categorySetup.count < categorySetup.availableOptionsCount
      ) {
        return categorySetup.count;
      }
      return Infinity;
    });

    const minSetup = min ? balanceSetup[min] : null;
    if (minSetup && minSetup.availableOptionsCount > minSetup.count) {
      minSetup.count += 1;
      return;
    }

    const noOthersMatch = order.find((key) => {
      const categorySetup = balanceSetup[key];
      return (
        config[key]?.showIfNoOthersMatch &&
        categorySetup &&
        categorySetup.count < categorySetup.availableOptionsCount
      );
    });
    if (noOthersMatch) {
      balanceSetup[noOthersMatch]!.count += 1;
    }
  });

  return _.mapValues(balanceSetup, (setup) => setup?.count) as Record<
    (typeof order)[number],
    number
  >;
}

const MAX_OPTIONS_TO_SHOW = 8;

const MAX_HEIGHT = 536;

type State = {
  selectedEntityCategory: EntitySelectCategory | null;
  isMenuOpen: boolean;
};

type Action =
  | { type: 'selectCategory'; category: EntitySelectCategory }
  | { type: 'goBack' }
  | { type: 'openMenu' }
  | { type: 'closeMenu' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'selectCategory':
      return { ...state, selectedEntityCategory: action.category };
    case 'goBack':
      return { ...state, selectedEntityCategory: null };
    case 'openMenu':
      return { ...state, isMenuOpen: true };
    case 'closeMenu':
      return { ...state, isMenuOpen: false, selectedEntityCategory: null };
    default:
      return state;
  }
};

export type CategorizedSelectCustomProps = {
  onHeaderClick: (category?: EntitySelectCategory) => void;
  selectedEntityCategory: EntitySelectCategory | null;
};

type SelectForCategorizedEntitySelectProps<
  IsMulti extends boolean,
  IsClearable extends boolean,
> = SelectProps<
  EntitySelectCategorizedSearchResult,
  IsMulti,
  false,
  EntityCategoryOption,
  IsClearable
>;

type CategorizedEntitySelectProps<
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
> = Omit<
  SelectForCategorizedEntitySelectProps<IsMulti, IsClearable>,
  | 'simpleValue'
  | 'value'
  | 'creatable'
  | 'menuIsOpen'
  | 'onMenuOpen'
  | 'onMenuClose'
  | 'onKeyDown'
> & {
  autoFocus?: boolean;
  config: EntitySelectConfig;
  PostPillComponent?: React.ElementType;
  value?: ValueType<BaseValue, IsMulti, false>;
  onCreateOption?: (input: string) => void;
  isValidNewOption?: (input: string) => boolean;
  showMoreResults?: boolean;
  maxOptionsToShow?: number;
} & (
    | { menuIsOpen?: never; onMenuOpen?: never; onMenuClose?: never }
    | Required<
        Pick<
          SelectForCategorizedEntitySelectProps<IsMulti, IsClearable>,
          'menuIsOpen' | 'onMenuOpen' | 'onMenuClose'
        >
      >
  );

const CategorizedEntitySelect = <
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
>({
  autoFocus,
  config,
  inputValue: controlledInputValue,
  onInputChange: controlledOnInputChange,
  components,
  className,
  showMoreResults = true,
  maxOptionsToShow = MAX_OPTIONS_TO_SHOW,
  openMenuOnClick = true,
  ...props
}: CategorizedEntitySelectProps<IsMulti, IsClearable>) => {
  const [{ selectedEntityCategory, isMenuOpen }, dispatch] = useReducer(
    reducer,
    { selectedEntityCategory: null, isMenuOpen: !!autoFocus },
  );

  if (
    typeof props.menuIsOpen === 'boolean' &&
    isMenuOpen !== props.menuIsOpen
  ) {
    dispatch({ type: props.menuIsOpen ? 'openMenu' : 'closeMenu' });
  }

  const [internalValue, setInternalValue] = useState<
    ValueType<BaseValue, IsMulti, false> | undefined
  >(props.value);
  const currentValue = 'value' in props ? props.value : internalValue;

  const [selfControlledInputValue, setSelfControlledInputValue] = useState('');
  const inputValue = controlledInputValue ?? selfControlledInputValue;

  const groupsMap = useAllGroupMap({ skip: !config.group });
  const postsMap = useAllSlimPostMap();
  const topicsMap = useAllTopicMap();
  const usersMap = useAllUsersMap();

  const valueToEntity = useCallback(
    (base: BaseValue): BaseEntitySearchResult | undefined => {
      const { type, id } = base;
      let entity;

      if (type === 'group') {
        const object = groupsMap[id];
        entity = object && groupToEntity(object);
      } else if (type === 'post') {
        const object = postsMap[id];
        entity = object && postToEntity(object);
      } else if (type === 'topic') {
        const object = topicsMap[id];
        entity = object && topicToEntity(object);
      } else if (type === 'user') {
        const object = usersMap[id];
        entity = object && userToEntity(object);
      }

      return entity ? { ...base, ...entity } : undefined;
    },
    [groupsMap, postsMap, topicsMap, usersMap],
  );

  const value = useMemo(
    () =>
      currentValue &&
      (props.isMulti && Array.isArray(currentValue)
        ? _.compact(currentValue.map((base) => valueToEntity(base)))
        : valueToEntity(currentValue as BaseValue)),
    [currentValue, props.isMulti, valueToEntity],
  ) as
    | ValueType<EntitySelectCategorizedSearchResult, IsMulti, false>
    | undefined;

  const baseOptions = useEntitySearch(
    inputValue,
    {
      ...config,
      order: config.order?.filter(
        (key): key is LocalEntitySearchType => key !== 'template',
      ),
    },
    ENTITY_SEARCH_OPTIONS,
  );

  const options: EntityCategoryOption[] = useMemo(() => {
    // If isMulti, filter out already selected options
    const filteredBaseOption =
      props.isMulti && Array.isArray(value)
        ? baseOptions.filter(
            (option) =>
              !value.some(
                (internalValueItem) =>
                  internalValueItem.type === option.type &&
                  internalValueItem.id === option.id,
              ),
          )
        : baseOptions;

    const groupedOptionsPerCategory = filteredBaseOption
      .map<EntitySelectCategorizedSearchResult>((result) =>
        result.type !== 'post'
          ? result
          : _.updateWith(result, 'post.topics', (topics) =>
              topics.map(pruneTopicFields),
            ),
      )
      .filter((option) =>
        selectedEntityCategory ? option.type === selectedEntityCategory : true,
      )
      .reduce<Record<string, EntitySelectCategorizedSearchResult[]>>(
        (acc, item) => {
          const mappedType =
            item.type === 'post' && item.post.type === PostType.Template
              ? 'template'
              : item.type;

          if (acc[mappedType]) {
            acc[mappedType]?.push(item);
          } else {
            acc[mappedType] = [item];
          }
          return acc;
        },
        {},
      );

    const categoryOptions: EntityCategoryOption[] = (
      config.order ?? ['new', 'post', 'template', 'topic', 'user', 'group']
    )
      .filter((key) => config[key])
      .map<EntityCategoryOption>((key) => {
        if (key === 'template') {
          const templateOptions = groupedOptionsPerCategory.template ?? [];
          const templateFilter = config.template?.filter;
          const filteredOptions = templateFilter
            ? templateOptions.filter(
                (option) =>
                  option.type === 'post' && templateFilter(option.post),
              )
            : templateOptions;
          return {
            type: 'template',
            label: config[key]?.label ?? inflect(key, 2),
            options: filteredOptions,
            sliced: false,
          };
        }
        if (key === 'new') {
          const newGroupOptions = config.new?.getGroup?.(inputValue);
          if (newGroupOptions) {
            return {
              type: 'new',
              label: newGroupOptions.label,
              options:
                newGroupOptions.options.map<EntitySelectCategorizedSearchResult>(
                  (option, index) => ({
                    ...option,
                    id: `${index}-new`,
                    type: 'new',
                    name: option.name,
                  }),
                ),
              sliced: false,
            };
          }
        }
        return {
          type: key as EntitySelectCategory,
          label: config[key]?.label ?? inflect(key, 2),
          options: [
            ...(groupedOptionsPerCategory[key as EntitySelectCategory] ?? []),
          ],
          sliced: false,
        };
      });

    if (selectedEntityCategory) {
      return categoryOptions.filter(
        (option) => option.type === selectedEntityCategory,
      );
    }

    const balancedCategories = balanceCategories({
      maxOptionsToShow,
      categoryOptions,
      config,
    });

    return categoryOptions
      .filter((option) => option.options.length > 0)
      .map((groupOption) => {
        const slicedOptions = groupOption.options.slice(
          0,
          balancedCategories[groupOption.type],
        );
        return {
          ...groupOption,
          options: slicedOptions,
          sliced:
            showMoreResults &&
            slicedOptions.length < groupOption.options.length,
        };
      });
  }, [
    baseOptions,
    config,
    inputValue,
    maxOptionsToShow,
    props.isMulti,
    selectedEntityCategory,
    showMoreResults,
    value,
  ]);

  const handleInputChange = (
    newInputValue: string,
    actionMeta: InputActionMeta,
  ) => {
    if (['input-blur', 'menu-close'].includes(actionMeta.action)) return;
    if (controlledOnInputChange) {
      controlledOnInputChange(newInputValue, actionMeta);
    } else {
      setSelfControlledInputValue(newInputValue);
    }
  };

  return (
    <Select<
      EntitySelectCategorizedSearchResult,
      IsMulti,
      false,
      EntityCategoryOption
    >
      autoFocus={autoFocus}
      className={classNames(
        className,
        props.isMulti ? styles.multiEntitySelect : styles.entitySelect,
        styles.categorizedEntitySelect,
      )}
      components={{
        DropdownIndicator: null,
        Option: EntitySelectCategoryOption,
        MultiValue: props.isMulti ? EntitySelectMultiValue : undefined,
        GroupHeading: EntitySelectCategoryHeading,
        MenuList: EntitySelectMenuList,
        NoOptionsMessage: EntitySelectNoOptionsMessage,

        // workaround as described here https://github.com/JedWatson/react-select/issues/3068#issuecomment-737531808
        Input:
          !openMenuOnClick && !props.controlShouldRenderValue
            ? NonHiddenInput
            : selectComponents.Input,

        ...components,
      }}
      maxMenuHeight={MAX_HEIGHT}
      getOptionLabel={({ name }: { name: string }) => name}
      getOptionValue={({ id }: { id: string }) => id}
      inputValue={inputValue}
      menuIsOpen={isMenuOpen}
      onInputChange={handleInputChange}
      onMenuClose={() => dispatch({ type: 'closeMenu' })}
      onMenuOpen={() => dispatch({ type: 'openMenu' })}
      simpleValue={false}
      {...props}
      filterOption={null}
      openMenuOnClick={openMenuOnClick}
      onChange={(newValue, action) => {
        const newOption: EntitySelectCategorizedSearchResult = Array.isArray(
          newValue,
        )
          ? newValue[0]
          : newValue;
        if (newOption?.type === 'new') {
          handleInputChange('', {
            action: 'input-change',
            prevInputValue: inputValue,
          });
          newOption.onSelect();
          return;
        }
        setInternalValue(newValue);
        props.onChange?.(newValue, action);
      }}
      // @ts-expect-error
      onHeaderClick={(category?: EntitySelectCategory) => {
        if (selectedEntityCategory || !category) {
          dispatch({ type: 'goBack' });
          return;
        }
        dispatch({ type: 'selectCategory', category });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && isMenuOpen) {
          e.stopPropagation();
        }
      }}
      selectedEntityCategory={selectedEntityCategory}
      options={options}
      value={value}
    />
  );
};

export default CategorizedEntitySelect;
